import type {
  AppUser,
  Assessment,
  CostCenter,
  Dashboard,
  FilePreviewResponse,
  ModuleContent,
  ProgressSummary,
  Question,
  ResultAttempt,
  ResultResponse,
  SessionUser,
  TrainingModule
} from "../types";

type RequestOptions = {
  method?: string;
  body?: unknown;
};

const jsonHeaders = { "Content-Type": "application/json" };
const API_URL = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");
const TOKEN_KEY = "setup_token";

type AuthResponse = {
  user: SessionUser;
  token?: string;
};

export type DownloadedFile = {
  blob: Blob;
  fileName: string;
  mimeType: string;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const isForm = options.body instanceof FormData;
  const response = await fetch(`${API_URL}/api${path}`, {
    method: options.method ?? "GET",
    credentials: "include",
    headers: buildHeaders(isForm, Boolean(options.body)),
    body: isForm ? options.body : options.body ? JSON.stringify(options.body) : undefined
  });

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json") ? await response.json() : await response.text();

  if (!response.ok) {
    const message =
      typeof payload === "object" && payload && "error" in payload
        ? String((payload as { error: string }).error)
        : "Nao foi possivel concluir a operacao.";
    throw new Error(message);
  }

  return payload as T;
}

async function requestFile(path: string): Promise<DownloadedFile> {
  const response = await fetch(`${API_URL}/api${path}`, {
    credentials: "include",
    headers: buildHeaders(false, false)
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    const payload = contentType.includes("application/json") ? await response.json() : await response.text();
    const message =
      typeof payload === "object" && payload && "error" in payload
        ? String((payload as { error: string }).error)
        : "Nao foi possivel carregar o arquivo.";
    throw new Error(message);
  }

  const blob = await response.blob();
  const mimeType = response.headers.get("content-type") ?? blob.type;
  return {
    blob,
    mimeType,
    fileName: parseContentDispositionFileName(response.headers.get("content-disposition")) ?? "arquivo"
  };
}

function buildHeaders(isForm: boolean, hasBody: boolean): HeadersInit | undefined {
  const token = readAuthToken();
  if (isForm) return token ? { Authorization: `Bearer ${token}` } : undefined;
  if (!hasBody) return token ? { Authorization: `Bearer ${token}` } : undefined;
  return token ? { ...jsonHeaders, Authorization: `Bearer ${token}` } : jsonHeaders;
}

function readAuthToken(): string {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(TOKEN_KEY) ?? "";
}

function saveAuthToken(token?: string): void {
  if (typeof window === "undefined" || !token) return;
  window.localStorage.setItem(TOKEN_KEY, token);
}

function clearAuthToken(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(TOKEN_KEY);
}

function parseContentDispositionFileName(value: string | null): string | null {
  if (!value) return null;

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/^"|"$/g, ""));

  const match = value.match(/filename="?([^"]+)"?/i);
  if (!match?.[1]) return null;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export const api = {
  login: async (email: string, password: string) => {
    const response = await request<AuthResponse>("/auth/login", { method: "POST", body: { email, password } });
    saveAuthToken(response.token);
    return response;
  },
  register: (payload: {
    nome: string;
    email: string;
    centro_custo: string;
    password: string;
    confirmPassword: string;
  }) =>
    request<AuthResponse>("/auth/register", { method: "POST", body: payload }).then((response) => {
      saveAuthToken(response.token);
      return response;
    }),
  publicCostCenters: () => request<CostCenter[]>("/auth/cost-centers"),
  logout: async () => {
    try {
      return await request<{ ok: true }>("/auth/logout", { method: "POST" });
    } finally {
      clearAuthToken();
    }
  },
  me: () => request<{ user: SessionUser }>("/auth/me"),
  trackActivity: (payload: {
    id_acesso: string;
    tela: string;
    id_modulo?: string;
    inicio: string;
    tempo_ativo_segundos: number;
  }) => request<void>("/activity", { method: "POST", body: payload }),
  dashboard: () => request<Dashboard>("/admin/dashboard"),
  costCenters: () => request<CostCenter[]>("/cost-centers"),
  createCostCenter: (payload: Partial<CostCenter>) =>
    request<CostCenter>("/cost-centers", { method: "POST", body: payload }),
  updateCostCenter: (id: string, payload: Partial<CostCenter>) =>
    request<CostCenter>(`/cost-centers/${id}`, { method: "PATCH", body: payload }),
  deleteCostCenter: (id: string) => request<void>(`/cost-centers/${id}`, { method: "DELETE" }),
  users: () => request<AppUser[]>("/users"),
  createUser: (payload: Record<string, unknown>) => request<AppUser>("/users", { method: "POST", body: payload }),
  updateUser: (id: string, payload: Record<string, unknown>) =>
    request<AppUser>(`/users/${id}`, { method: "PATCH", body: payload }),
  deleteUser: (id: string) => request<void>(`/users/${id}`, { method: "DELETE" }),
  modules: () => request<TrainingModule[]>("/modules"),
  createModule: (payload: Record<string, unknown>) =>
    request<TrainingModule>("/modules", { method: "POST", body: payload }),
  updateModule: (id: string, payload: Record<string, unknown>) =>
    request<TrainingModule>(`/modules/${id}`, { method: "PATCH", body: payload }),
  deleteModule: (id: string) => request<void>(`/modules/${id}`, { method: "DELETE" }),
  contents: (moduleId: string) => request<ModuleContent[]>(`/modules/${moduleId}/contents`),
  createContent: (moduleId: string, payload: Record<string, unknown>) =>
    request<ModuleContent>(`/modules/${moduleId}/contents`, { method: "POST", body: payload }),
  uploadContent: (moduleId: string, payload: FormData) =>
    request<ModuleContent>(`/modules/${moduleId}/contents/upload`, { method: "POST", body: payload }),
  updateContent: (id: string, payload: Record<string, unknown>) =>
    request<ModuleContent>(`/contents/${id}`, { method: "PATCH", body: payload }),
  deleteContent: (id: string, deleteDriveFile = false) =>
    request<void>(`/contents/${id}?deleteDriveFile=${deleteDriveFile}`, { method: "DELETE" }),
  assessments: (moduleId: string) => request<Assessment[]>(`/modules/${moduleId}/assessments`),
  createAssessment: (moduleId: string, payload: Record<string, unknown>) =>
    request<Assessment>(`/modules/${moduleId}/assessments`, { method: "POST", body: payload }),
  updateAssessment: (id: string, payload: Record<string, unknown>) =>
    request<Assessment>(`/assessments/${id}`, { method: "PATCH", body: payload }),
  deleteAssessment: (id: string) => request<void>(`/assessments/${id}`, { method: "DELETE" }),
  questions: (assessmentId: string) => request<Question[]>(`/assessments/${assessmentId}/questions`),
  createQuestion: (assessmentId: string, payload: Record<string, unknown>) =>
    request<Question>(`/assessments/${assessmentId}/questions`, { method: "POST", body: payload }),
  updateQuestion: (id: string, payload: Record<string, unknown>) =>
    request<Question>(`/questions/${id}`, { method: "PATCH", body: payload }),
  deleteQuestion: (id: string) => request<void>(`/questions/${id}`, { method: "DELETE" }),
  completeContent: (id_modulo: string, id_conteudo: string) =>
    request<void>("/progress/complete", { method: "POST", body: { id_modulo, id_conteudo } }),
  downloadFile: (fileId: string) => requestFile(`/files/${fileId}/download`),
  previewFile: (fileId: string) => request<FilePreviewResponse>(`/files/${fileId}/preview`),
  progress: () => request<ProgressSummary>("/progress/me"),
  submitAssessment: (assessmentId: string, answers: { id_pergunta: string; resposta_usuario: string | string[] }[]) =>
    request<ResultAttempt>(`/assessments/${assessmentId}/submit`, { method: "POST", body: { answers } }),
  results: () => request<ResultAttempt[]>("/results"),
  responses: (attemptId: string) => request<ResultResponse[]>(`/results/${attemptId}/responses`),
  manualGrade: (responseId: string, payload: { pontuacao_obtida: number; feedback: string }) =>
    request<Record<string, unknown>>(`/responses/${responseId}/grade`, { method: "PATCH", body: payload })
};
